import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
} from '@fluentui/react-icons';
import {
  SpinnerIcon,
  HiddenDivForAnnounce,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { usePromptControlViewFactoryWithState } from './prompt-control-state';
import { useColorControlViewFactoryWithState } from './color-control-state';

export const tabs = {
  AI: 'AI',
  SolidColor: 'SolidColor',
};


export const useControlViewFactoryWithState = ({
  i18n,
  originalImage,
  resultImage,
  imageUrlWithoutBackground,
  imageUrlWithGeneratedBackground,
  setImageUrlWithGeneratedBackground,
  segmentationMasks,
  getSegmentations,
  generateBackground,
  generateBackgrounds,
  drawColorBackground,
  imageUrlWithColoredBackground,
  isFetchingSegmentations,
  fetchSegmentationError,
  goToImageAdjustView,
  selectedMasks,
  setSelectedMasks,
  setSelectedImageSrc,
  maskValues,
  MultiLineEllipsisText,
  selectedImageSrc,
  getMaskValueAtPoint,
  logger,
  applyImage,
  permissions,
}) => {
  // Generate the image with the removed background (once when entering the page)
  const [isCallRemovingBackground, setIsCallRemovingBackground] = useState(true);
  const isRemovingBackground = useMemo(() =>
    isCallRemovingBackground || isFetchingSegmentations, [isCallRemovingBackground, isFetchingSegmentations]);

  const [notificationConfig, setNotificationConfig] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tabs.AI);

  const dismissNotification = useCallback(() => {
    setNotificationConfig(null);
  }, []);

  const onTabSelect = useCallback((event, tab) => {
    dismissNotification();
    logger.logTrace('background change: switch the tab', { tab: tab.value });
    setSelectedTab(tab.value);
  }, [dismissNotification, logger]);

  const buttonsRef = useRef(null);
  const image = useMemo(() => ({
    url: originalImage.url,
    width: originalImage.width,
    height: originalImage.height,
  }), [originalImage]);

  const removingBackgroundNotificationConfig = useMemo(() => ({
    icon: <SpinnerIcon i18n={i18n} />,
    content: (
      <>
        <Text weight="semibold">{i18n.getString(_TL_('Removing background'))}</Text>
        <HiddenDivForAnnounce toAnnounce={i18n.getString(_TL_('Removing background'))} />
      </>
    ),
  }), [i18n]);

  const backgroundRemovedNotificationConfig = useMemo(() => {
    const content = (
      <div className="notification-content">
        <Text weight="semibold">{i18n.getString(_TL_('We\'ve removed the background. '))}</Text>
        <Text>{i18n.getString(_TL_('You can refine the subject area to keep or remove certain subjects and objects by selecting Edit subject area.'))}</Text>
        <HiddenDivForAnnounce toAnnounce={i18n.getString(_TL_('We\'ve removed the background. '))} />
      </div>
    );
    return ({
      icon: <CheckmarkCircle16Filled className="check-mark-icon" />,
      content,
      onDismiss: dismissNotification,
      dismissAriaLabel: i18n.getString(_TL_('Close')),
    });
  }, [dismissNotification, i18n]);

  // fetch the segmentation when entering the page
  useEffect(() => {
    setIsCallRemovingBackground(true);
    setNotificationConfig(removingBackgroundNotificationConfig);
    if (image.url) {
      getSegmentations({
        imageUrl: image.url,
        imageWidth: image.width,
        imageHeight: image.height,
      }).then(() => {
        setIsCallRemovingBackground(false);
        setNotificationConfig(backgroundRemovedNotificationConfig);
      });
    }
  }, [backgroundRemovedNotificationConfig, getSegmentations, i18n, image.height, image.url, image.width, removingBackgroundNotificationConfig]);


  const {
    promptControlView,
    buttonsConfig: promptButtonsConfig,
    imageView: promptImageView,
  } = usePromptControlViewFactoryWithState({
    i18n,
    image,
    segmentationMasks,
    imageUrlWithoutBackground,
    setSelectedImageSrc,
    selectedMasks,
    setSelectedMasks,
    selectedImageSrc,
    maskValues,
    buttonsRef,
    setNotificationConfig,
    getMaskValueAtPoint,
    logger,
    generateBackground,
    generateBackgrounds,
    imageUrlWithGeneratedBackground,
    setImageUrlWithGeneratedBackground,
    fetchSegmentationError,
    isRemovingBackground,
    isFetchingSegmentations,
    resultImage,
    goToImageAdjustView,
    MultiLineEllipsisText,
    applyImage,
    permissions,
  });

  const {
    colorControlView,
    buttonsConfig: colorButtonsConfig,
    imageView: colorImageView,
  } = useColorControlViewFactoryWithState({
    i18n,
    image,
    logger,
    selectedImageSrc,
    drawColorBackground,
    imageUrlWithColoredBackground,
    imageUrlWithoutBackground,
    goToImageAdjustView,
    applyImage,
  });

  const buttonsConfig = selectedTab === tabs.AI ? promptButtonsConfig : colorButtonsConfig;
  const imageView = selectedTab === tabs.AI ? promptImageView : colorImageView;

  return {
    promptControlView,
    colorControlView,
    buttonsConfig,
    imageView,
    notificationConfig,
    selectedTab,
    onTabSelect,
    buttonsRef,
    isRemovingBackground,
  };
};
